import {getToken, clearToken} from '@/utils/authToken';

/**
 * API host and credential — from `.env` ONLY (see `.env.example`).
 *
 * Both were hardcoded here until 2026-08-26, in four places between them, so rotating the
 * key or pointing a build at another host meant editing source. They now use the same
 * variable names as the other three portals: VITE_API_BASE_URL / VITE_API_KEY.
 *
 * NO fallback on purpose. A build with the var missing must fail loudly rather than quietly
 * ship a stale key — the documented key already returns 401 against the live API, and a
 * fallback would make that look like an app bug instead of a config one.
 */
// In dev, the same path RELATIVE to the dev server, which proxies `/api` to the host —
// see `server.proxy` in vite.config.js. Builds call the host directly.
const API_BASE_URL = import.meta.env.DEV
  ? new URL(import.meta.env.VITE_API_BASE_URL).pathname
  : import.meta.env.VITE_API_BASE_URL;
const API_KEY = import.meta.env.VITE_API_KEY;

// Siblings of the configured base, derived rather than configured separately so there is
// exactly ONE host to change: the `/api/v1` root is the base minus its `/passengers`
// segment, and auth hangs off the base.
const API_ROOT = API_BASE_URL.replace(/\/passengers\/?$/, '');
const API_BASE_URL_AUTH = `${API_BASE_URL}/auth`;

const API_HEADERS = {
  Accept: 'application/vnd.api+json',
  'Content-Type': 'application/vnd.api+json',
  'X-API-Key': API_KEY,
};

const API_HEADERS_JSON = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'X-API-Key': API_KEY,
};

function getAuthHeaders(baseHeaders, extraHeaders = {}) {
  const headers = {...baseHeaders, ...extraHeaders};
  if (!headers.Authorization) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

async function request(baseURL, defaultHeaders, endpoint, options = {}) {
  const url = endpoint.startsWith('http') ? endpoint : `${baseURL}${endpoint}`;
  const isFormData = options._isFormData;

  const headers = getAuthHeaders(defaultHeaders, options.headers);
  if (isFormData) {
    delete headers['Content-Type'];
    headers['Accept'] = 'application/json';
  }

  const config = {...options, headers};
  delete config._isFormData;

  try {
    const response = await fetch(url, config);

    let data = null;
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      try { data = await response.json(); } catch { data = null; }
    }

    // Any 401 from our backend means the session is invalid/expired — the cookie may
    // already be gone (it lapsed, or the user logged out in another tab), so don't
    // gate this on getToken(). Drop the cookie and signal AuthContext, which flips to
    // signed-out so ProtectedRoute navigates to /signin (it also saves the return
    // path) — instead of leaving the page to render broken. Skip on the auth pages so
    // the sign-in/sign-up forms can surface their own "invalid credentials" errors.
    if (response.status === 401) {
      const onAuthPage = ['/signin', '/signup', '/otp-verification', '/authorize']
        .some((p) => window.location.pathname.startsWith(p));
      if (!onAuthPage) {
        clearToken();
        window.dispatchEvent(new Event('hopn:unauthorized'));
        return {success: false, data: null, error: 'Session expired', statusCode: 401};
      }
    }

    if (!response.ok) {
      return {success: false, data: null, error: data?.message || `Request failed with status ${response.status}`, statusCode: response.status, raw: data};
    }

    return {success: true, data: data?.data !== undefined ? data.data : data, key: data?.key || null, message: data?.message || null, error: null, statusCode: response.status};
  } catch (error) {
    return {success: false, data: null, error: 'Network error. Please check your connection and try again.', statusCode: 0, raw: error};
  }
}

function createApiClient(baseURL, defaultHeaders = API_HEADERS) {
  const req = (endpoint, options) => request(baseURL, defaultHeaders, endpoint, options);

  return {
    get: (endpoint, options = {}) => req(endpoint, {...options, method: 'GET', cache: 'no-store'}),

    post: (endpoint, body, options = {}) => req(endpoint, {...options, method: 'POST', body: JSON.stringify(body)}),

    postJson: (endpoint, body, options = {}) => request(baseURL, API_HEADERS_JSON, endpoint, {...options, method: 'POST', body: JSON.stringify(body)}),

    postFormData: (endpoint, formData, options = {}) => req(endpoint, {...options, method: 'POST', body: formData, _isFormData: true}),

    delete: (endpoint, options = {}) => req(endpoint, {...options, method: 'DELETE'}),
  };
}

export const apiClient = createApiClient(API_BASE_URL);
export const wpApiClient = createApiClient(API_ROOT);
export const customersApiClient = createApiClient(API_BASE_URL);
export const authApiClient = createApiClient(API_BASE_URL_AUTH);

export default apiClient;
